import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import CustomStepper from "../../Components/Others/custom-stepper";

import useStakeCheckingStatusAction from "../../CustomHooks/useStakeCheckingStatusAction";
import usePinWheel from "../../CustomHooks/usePinWheel";

import { useGetRecurringDeposit, useStakeDebit } from "../../CustomHooks/Queries";

import {
  getStepIcon,
  isSVDApproved,
  isSVDDenied,
  isSVDEligible,
  isSVDIneligible,
  isSVDPending,
  isSVDPendingVerification,
} from "../../Utils/helpers";

import { PiggyBank, CalendarIllustration } from "../../Assets/Images";
import { CalendarChecked, Card, CashIn, ChevronRight } from "../../Assets/Icons";

export default function PayRentWithStakePay2() {
  const navigate = useNavigate();
  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const { show: showIntercom } = useIntercom();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit, isError: isStakeDebitError } = useStakeDebit();

  const IsSVDApproved = useMemo(() => isSVDApproved(stakeDebit?.data), [stakeDebit?.data]);
  const IsSVDEligible = useMemo(() => isSVDEligible(stakeDebit?.data), [stakeDebit?.data]);
  const IsSVDPending = useMemo(() => isSVDPending(stakeDebit?.data), [stakeDebit?.data]);
  const IsSVDPendingVerification = useMemo(() => isSVDPendingVerification(stakeDebit?.data), [stakeDebit?.data]);
  const IsSVDDenied = useMemo(() => isSVDDenied(stakeDebit?.data), [stakeDebit]);
  const IsSVDIneligible = useMemo(() => isSVDIneligible(stakeDebit?.data), [stakeDebit]);

  const { isLoadingPayrollAccounts, payrollAccountsData } = usePinWheel(IsSVDApproved);
  const { isLoading: isLoadingRecurringDeposit, data: recurringDeposit } = useGetRecurringDeposit();

  const isPayrollConnected = useMemo(() => {
    return payrollAccountsData?.data?.data?.accounts?.length > 0;
  }, [payrollAccountsData]);

  const isRecurringDepositActive = useMemo(() => {
    return recurringDeposit?.data?.data?.active ?? false;
  }, [recurringDeposit]);

  const isLoading = isLoadingStakeDebit || isLoadingPayrollAccounts || isLoadingRecurringDeposit;
  const isError = isStakeDebitError;

  const activeStep = useMemo(() => {
    if (IsSVDApproved && (isPayrollConnected || isRecurringDepositActive)) return 2;
    else return 1;
  }, [IsSVDApproved, isPayrollConnected, isRecurringDepositActive]);

  const handleOnStepActionButtonClick = () => {
    if (IsSVDDenied) {
      showIntercom();
    } else {
      stakeCheckingStatusAction(stakeDebit?.data);
    }
  };

  const steps = [
    ...(!IsSVDApproved
      ? [
          {
            label: "Open a Stake account",
            StepComponent: () => getStepIcon(<Card />),
            subComponent: (
              <div className="step-details">
                <p className="description">
                  {IsSVDEligible
                    ? "FDIC-insured with a virtual Stake Visa Debit card. Unlock Cash Back on rent and purchases"
                    : IsSVDPending
                      ? "Your Stake Debit application is pending."
                      : IsSVDDenied
                        ? "Your Stake Debit application was denied."
                        : IsSVDPendingVerification
                          ? "Your Stake Debit application requires additional verification."
                          : ""}
                </p>
                <button className="step-action" onClick={handleOnStepActionButtonClick}>
                  {IsSVDEligible
                    ? "Get Started"
                    : IsSVDPending
                      ? "View Status"
                      : IsSVDDenied
                        ? "Contact Support"
                        : IsSVDPendingVerification
                          ? "Verify"
                          : ""}
                  <ChevronRight />
                </button>
              </div>
            ),
          },
        ]
      : []),
    {
      label: "Fund your account",
      StepComponent: () => getStepIcon(<CashIn />),
      subComponent: (
        <div className="step-details">
          <p className="description">Choose how you want to fund your Stake account to pay rent.</p>
          {IsSVDApproved && !isPayrollConnected && !isRecurringDepositActive && (
            <div className="funding-cards">
              <div className="funding-card blue">
                <div className="funding-card-content">
                  <div className="funding-card-texts">
                    <div className="funding-card-title">Direct Deposit</div>
                    <div className="funding-card-description">
                      It's your money, get it sooner. Get your paycheck up to two days early.
                    </div>
                  </div>
                  <button className="funding-card-button" onClick={() => navigate("/banking/direct-deposit")}>
                    Get Started <ChevronRight />
                  </button>
                </div>
                <img src={PiggyBank} className="funding-card-illustration" alt="" />
              </div>
              <div className="funding-card pink">
                <div className="funding-card-content">
                  <div className="funding-card-texts">
                    <div className="funding-card-title">Recurring deposit</div>
                    <div className="funding-card-description">
                      It's your money, get it sooner. Get your paycheck up to two days early.
                    </div>
                  </div>
                  <button className="funding-card-button" onClick={() => navigate("/banking/recurring-deposits")}>
                    Get Started <ChevronRight />
                  </button>
                </div>
                <img src={CalendarIllustration} className="funding-card-illustration" alt="" />
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      label: "Pay rent",
      StepComponent: () => getStepIcon(<CalendarChecked />),
      subComponent: (
        <div className="step-details">
          <p className="description">Schedule your payment once funds are available.</p>
        </div>
      ),
    },
  ];

  return isLoading || isError || IsSVDIneligible ? (
    <></>
  ) : (
    <div className="pay-rent-with-stake">
      <h3 className="pay-rent-title">Pay Rent</h3>
      <CustomStepper className="with-content enlarged" steps={steps} activeStep={activeStep} orientation="vertical" />
    </div>
  );
}
